import { InfoImage } from '@infinex-private/connect-web-shared/components';
import { CheckIcon, EditIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Pressable, Text } from '@infinex/ui/components';

import type { NormalizedAssetApproval as AssetApprovalItemData } from './normalizeChainResponse';

type AssetApprovalItemProps = AssetApprovalItemData & {
  /** Whether this approval can be edited */
  isEditable?: boolean;
  /** Whether this approval has been modified */
  isModified?: boolean;
  /** The modified amount to display (formatted) */
  modifiedAmount?: string;
  /** Called when the edit button is pressed */
  onEditPress?: () => void;
};

export function AssetApprovalItem({
  asset,
  amount,
  usdValue,
  isEditable = false,
  isModified = false,
  modifiedAmount,
  onEditPress,
}: AssetApprovalItemProps) {
  const displayAmount = modifiedAmount ?? amount;
  const formattedUsd = usdValue === 'Unlimited' ? 'Unlimited' : `$${usdValue}`;

  const icon = <CheckIcon fill="highlight" className="bg-fillHighlight" />;

  const tokenIcon = (
    <InfoImage
      src={asset.logoUrl ?? ''}
      alt={asset.symbol}
      className="rounded-full"
      size={40}
      haloBg="#1C1E21"
      emblemPosition="top-left"
      badgeSize={16}
      haloSize={2}
      customEmblemIcon={icon}
    />
  );

  const content = (
    <div className="flex flex-1 items-center gap-3">
      {isEditable ? (
        <div className="relative size-10">
          <div className="transition-opacity duration-200 group-hover:opacity-0">
            {tokenIcon}
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <EditIcon size="medium" fill="emphasis" />
          </div>
        </div>
      ) : (
        tokenIcon
      )}

      <div className="flex flex-1 flex-col">
        <Text className="text-emphasis block text-xs font-medium">
          {isModified ? 'Modified Approval' : 'Approving'}
        </Text>
        <Text className="text-default block text-base font-semibold">
          {asset.name}
        </Text>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end text-right">
          {displayAmount === 'Unlimited' ? (
            <Text className="text-emphasis block text-base font-semibold">
              Unlimited
            </Text>
          ) : (
            <>
              <Text
                className={cn(
                  'block text-base font-semibold tabular-nums',
                  isModified ? 'text-brand' : 'text-emphasis'
                )}
              >
                {displayAmount}
              </Text>
              {isModified ? (
                <Text className="text-secondary block text-xs tabular-nums line-through">
                  {amount}
                </Text>
              ) : (
                <Text className="text-secondary block text-xs tabular-nums">
                  {formattedUsd}
                </Text>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (isEditable) {
    return (
      <Pressable
        onPress={onEditPress}
        className="pressed:bg-transparent group flex w-full cursor-pointer items-center gap-3 bg-transparent py-3 text-left first:pt-0 last:pb-0 hover:bg-transparent"
      >
        {content}
      </Pressable>
    );
  }

  return (
    <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
      {content}
    </div>
  );
}
